/**
 * Agent Registry
 *
 * Defines the flow graph for FAQ-only voice: greeting -> welcome -> faq.
 */

import { AgentNode, VoiceGender } from './types';
import {
  createGreetingAgent,
  createWelcomeAgent,
  createFAQAgent,
} from './nodes';

/**
 * Create all agents with the specified voice gender
 */
export function createAllAgents(voiceGender: VoiceGender = 'male'): Map<string, AgentNode> {
  const agents = new Map<string, AgentNode>();

  agents.set('greeting_agent', createGreetingAgent(voiceGender));
  agents.set('welcome_agent', createWelcomeAgent(voiceGender));
  agents.set('faq_agent', createFAQAgent(voiceGender));

  return agents;
}

/**
 * Get the initial agent ID (entry point)
 */
export function getInitialAgentId(): string {
  return 'greeting_agent';
}

/**
 * Get list of all agent IDs
 */
export function getAllAgentIds(): string[] {
  return ['greeting_agent', 'welcome_agent', 'faq_agent'];
}

/**
 * Agent flow metadata for debugging and visualization
 */
export const agentFlowMetadata = {
  entryPoint: 'greeting_agent',

  flows: {
    greeting: {
      agents: ['greeting_agent', 'welcome_agent'],
      description: 'Language detection and welcome message',
    },
    faq: {
      agents: ['faq_agent'],
      description: 'FAQ and question answering',
    },
  },

  transitions: [
    { from: 'greeting_agent', to: 'welcome_agent', trigger: 'language_detected' },
    { from: 'welcome_agent', to: 'faq_agent', trigger: 'proceed_to_faq' },
    { from: 'faq_agent', to: 'welcome_agent', trigger: 'back_to_menu' },
  ],
};

/**
 * Validate that all transitions reference valid agents
 */
export function validateRegistry(agents: Map<string, AgentNode>): string[] {
  const errors: string[] = [];
  const agentIds = new Set(agents.keys());

  for (const [agentId, agent] of agents) {
    for (const [transitionName, transition] of agent.exitGuardrails.transitions) {
      if (!agentIds.has(transition.target.agentId)) {
        errors.push(
          `Agent '${agentId}' has transition '${transitionName}' to unknown agent '${transition.target.agentId}'`
        );
      }
    }

    if (agent.exitGuardrails.fallback && !agentIds.has(agent.exitGuardrails.fallback.agentId)) {
      errors.push(
        `Agent '${agentId}' has fallback to unknown agent '${agent.exitGuardrails.fallback.agentId}'`
      );
    }
  }

  return errors;
}
